/**
 * @file   Source/gitcha_Web/src/components/Onboarding/Dashboard/FilterLocationSearch.js
 * @brief  This file is responsible for location search in home page
 * @date   April, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import InputGroupControl from "../../Common/InputGroup";
import { capitalizeString } from "../../../actions/common";
import { getGeoLocation } from "../../../services/commonServices";
import {
  preloadPlaces,
  getAutocompletePredictions,
} from "@/services/placesService";
import {
  getLocalStorage,
  getUserId,
  removeLocalStorage,
} from "../../../helpers/common";
import { loggedInUser } from "@/helpers";
import useAvailableCities from "../../../hooks/useAvailableCities";
import LocationSearchDropdown from "@/components/Common/LocationSearchDropdown";

const FilterLocationSearch = (props) => {
  const {
    location,
    setLocation,
    gitchaNotAvailble,
    checkGitchaAvailableInLoc,
    setUserLocation,
    userLocation,
  } = props;
  const { featuredCities, filterCities } = useAvailableCities();
  const [locationList, setLocationList] = useState([]);
  const [availableMarketResults, setAvailableMarketResults] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  const locationString = getLocalStorage("LocationString");
  const userId = getUserId();

  const user = loggedInUser();

  // Preload Places script on mount so it's ready when user starts typing
  useEffect(() => {
    preloadPlaces();
  }, []);

  useEffect(() => {
    if (locationString != null && userId) {
      //handle location search from webflow page
      const reqPayload = `address=${capitalizeString(
        locationString,
      )}&components=country:US|country:PR`;
      getAddresseDetails({ reqPayload: reqPayload });
    } else if (!location) {
      clearLocationStates();
    }
  }, [gitchaNotAvailble, locationString, userId]);

  // Show featured cities on focus when input is empty
  const handleFocus = () => {
    setIsFocused(true);
    if (!location || location.trim() === "") {
      setAvailableMarketResults(featuredCities);
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      setIsFocused(false);
      if (!location || location.trim() === "") {
        setAvailableMarketResults([]);
        setLocationList([]);
      }
    }, 200);
  };

  //clear searched address list
  const clearAddressList = () => {
    setLocationList([]);
    setLocation("");
  };

  //Handle text change in location field
  const handleLocationChange = (val) => {
    const value = capitalizeString(val);
    const reqPayload = `address=${value}&components=country:US|country:PR`;
    if (value.trim() !== "") {
      // Filter available Gitcha cities
      const filtered = filterCities(value.trim());
      setAvailableMarketResults(filtered.slice(0, 3));

      getAddresseDetails({ reqPayload: reqPayload, inputValue: val.trim() });
    } else {
      clearAddressList();
      setAvailableMarketResults(isFocused ? featuredCities : []);
    }
    if (value && value.trim() !== "") {
      setLocation(value);
    } else {
      clearLocationStates();
      // Don't reset userLocation on empty input - keep the feed visible
      // while the user is still typing. Location only changes on selection.
    }
  };

  //clear searched location states
  const clearLocationStates = () => {
    setLocation("");
  };

  //fetch details of location based on entered location
  const getAddresseDetails = async ({ eventType, reqPayload, inputValue }) => {
    const startsWithNumber = inputValue && /^\d/.test(inputValue);
    const isZipCode = inputValue && /^\d{3,5}$/.test(inputValue.trim());

    // locationString auto-select uses geocoding (resolves a known location)
    if (!inputValue && locationString) {
      const geoData = await getGeoLocation(reqPayload);
      const results = _.get(geoData, "results", []);
      const filteredResults = results.filter((r) => {
        const country = (r.address_components || []).find((c) =>
          c.types?.includes("country"),
        )?.long_name;
        return (
          (country === "United States" || country === "Puerto Rico") &&
          !(r.types || []).includes("country")
        );
      });
      if (filteredResults.length > 0) {
        handleItemSelection(filteredResults[0]);
        removeLocalStorage("LocationString");
      }
      return;
    }

    if (isZipCode) {
      // ZIP code: geocoding is best for exact ZIP resolution
      const geoData = await getGeoLocation(reqPayload);
      const results = _.get(geoData, "results", []);
      const filteredResults = results.filter((r) => {
        const country = (r.address_components || []).find((c) =>
          c.types?.includes("country"),
        )?.long_name;
        return (
          (country === "United States" || country === "Puerto Rico") &&
          !(r.types || []).includes("country")
        );
      });
      setLocationList(filteredResults.slice(0, 5));
      return;
    }

    // For both cities and addresses, use Places Autocomplete.
    // types: '(cities)' for letter input ranks major cities correctly.
    // types: 'address' for number input returns street addresses.
    const placesType = startsWithNumber ? ["address"] : ["(cities)"];
    const predictions = await getAutocompletePredictions(
      capitalizeString(inputValue),
      {
        types: placesType,
        componentRestrictions: { country: ["us", "pr"] },
      },
    );

    const items = predictions.map((p) => ({
      _isPlacesPrediction: true,
      place_id: p.place_id,
      formatted_address: p.description,
      types: p.types || [],
    }));

    setLocationList(items.slice(0, 5));
  };

  // Handle clicking an available market - geocode then use standard selection flow
  const handleAvailableMarketClick = async (market) => {
    const reqPayload = `address=${encodeURIComponent(market.city + ", " + market.state)}&components=country:US`;
    const geoData = await getGeoLocation(reqPayload);
    const results = _.get(geoData, "results", []);
    if (results?.length > 0) {
      handleItemSelection(results[0]);
    }
  };

  // Handle clicking a dropdown item - geocoding result or Places prediction.
  // For Places predictions, geocode the description to get full county info
  // (Places getDetails for cities often omits administrative_area_level_2).
  const handleDropdownClick = async (item) => {
    if (item._isPlacesPrediction) {
      const reqPayload = `address=${encodeURIComponent(item.formatted_address)}&components=country:US|country:PR`;
      const geoData = await getGeoLocation(reqPayload);
      const results = _.get(geoData, "results", []);
      if (results?.length > 0) {
        handleItemSelection(results[0]);
      }
    } else {
      handleItemSelection(item);
    }
  };

  //Handle location select from the list. Based on this location want ads will be listed
  const handleItemSelection = async (address) => {
    let city, state, county;

    const geometry = _.get(address, "geometry", "");
    const locBounds = _.get(geometry, "location", "");
    const formatted_address = _.get(address, "formatted_address", "");
    const firstAddressComponent = _.get(address, "address_components", []);

    const stateSearched = firstAddressComponent.filter(
      (ca) => ca.types?.[0] === "administrative_area_level_1",
    );
    if (stateSearched.length) {
      state = _.get(stateSearched?.[0], "short_name", "");
    }

    const locality = firstAddressComponent.filter(
      (ca) => ca.types?.[0] === "locality",
    );
    if (locality.length) {
      city = _.get(locality?.[0], "long_name", "");
    }

    const countyList = firstAddressComponent.filter(
      (ca) => ca.types?.[0] === "administrative_area_level_2",
    );
    if (countyList.length) {
      county = _.get(countyList?.[0], "short_name", "");
    }

    // Force PR for Puerto Rico
    const countryComponent = firstAddressComponent.find((ca) =>
      ca.types?.includes("country"),
    );
    if (countryComponent?.long_name === "Puerto Rico") {
      state = "PR";
    }

    setLocation(city != undefined ? `${city}, ${state}` : formatted_address);
    setAvailableMarketResults([]);
    const locationObject = {
      latitude: _.get(locBounds, "lat", ""),
      longitude: _.get(locBounds, "lng", ""),
      city: city,
      state: state,
      formatted_address:
        city != undefined ? `${city}, ${state}` : formatted_address,
    };

    checkGitchaAvailableInLoc(
      city,
      state,
      county,
      formatted_address,
      locationObject,
    );
    setLocationList([]);
  };

  const showDropdown =
    isFocused && (availableMarketResults.length > 0 || locationList.length > 0);

  return (
    <>
      <div className="position-relative">
        {locationList.length > 0 ? (
          <Button
            variant=""
            className="search-clear-btn"
            onClick={() => {
              clearAddressList();
              setAvailableMarketResults([]);
            }}
          ></Button>
        ) : (
          ""
        )}
        <InputGroupControl
          inputGroupClass="wantad-location-search position-relative"
          ariaLabel="Example"
          ariaDescribedBy="basic-addon3"
          placeholder="City, ZIP, or address"
          errorMessage=""
          value={location}
          iconClass="location-icon"
          isIconEnable={true}
          iconId="button-addon3"
          onChange={(e) => {
            handleLocationChange(e.target.value);
          }}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        {showDropdown && (
          <LocationSearchDropdown
            availableMarkets={availableMarketResults}
            placesResults={locationList}
            onAvailableMarketClick={handleAvailableMarketClick}
            onPlacesResultClick={handleDropdownClick}
          />
        )}
      </div>
    </>
  );
};
export default FilterLocationSearch;
